import { T, useTolgee, useTranslate } from '@tolgee/react';
import { Dropdown, Menu, Tooltip } from 'antd';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { ArrowDownSvg, ArrowLeftSvg, CheckSvg, PlusSvg } from '../../../assets/icons';
import MediaIconSvg from '../../../assets/icons/MediaIconSvg';
import PasteSvg from '../../../assets/icons/PasteSvg';
import UploadsSvg from '../../../assets/icons/UploadsSvg';
import YoutubeSvg from '../../../assets/icons/YoutubeSvg';
import theme from '../../../assets/theme';
import { useMetrics } from '../../../hooks/useMetrics';
import useRouterMatch from '../../../hooks/useRouterMatch';
import { getTrackingParams } from '../../../utils/facebookTracking';
import { TextArea } from '../../_atoms/_TextArea';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';

const rtlLangs = ['ar', 'he', 'fa', 'ur']; // Arabic, Hebrew, Persian, Urdu

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  margin: 0 auto;
  margin-bottom: 150px;

  @media (max-width: 768px) {
    margin: 0 auto;
    margin-bottom: 0;
  }
`;

const InputContainer = styled.div<{
  $isTextAreaFocused: boolean;
  $isHovered: boolean;
}>`
  width: 100%;
  max-width: 800px;
  margin-bottom: 20px;
  position: relative;
  background-color: ${theme.colors.white};
  box-shadow: 0px 1.6px 3.6px rgba(0, 0, 0, 0.17), 0px 0.3px 30px rgba(0, 0, 0, 0.17);
  border-radius: 10px;
  padding: 10px;
  border: 1px solid transparent;
  cursor: text;
  transition: all 0.5s ease;

  ${({ $isHovered }) =>
    $isHovered &&
    `
    border: 1px solid ${theme.colors.greyLight};
    box-shadow: 0px 1.6px 3.6px rgba(0, 0, 0, 0.23), 0px 0.3px 10px rgba(0, 0, 0, 0.21);
  `}
  ${({ $isTextAreaFocused }) =>
    $isTextAreaFocused &&
    `
    border: 1px solid ${theme.colors.grey};
    box-shadow: 0px 1.6px 3.6px rgba(0, 0, 0, 0.23), 0px 0.3px 10px rgba(0, 0, 0, 0.21);
  `}

  @media (max-width: 768px) {
    margin-bottom: 16px;
    padding: 12px;
    border-radius: 12px;
  }
`;

const FormContainer = styled.div`
  width: 100%;
  padding: 0px 20px 20px 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const SubmitButton = styled(Button)`
  width: fit-content;
  height: 30px;
  width: 30px;
  border-radius: 20px;
  padding: 20px;
  background: ${theme.colors.orangeGradient};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.white} !important;
  gap: 10px;

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: ${theme.effects.floatingCreatorElementShadow};
    background: linear-gradient(45deg, #ffa55b, #ffc107);
    background-size: 400% 400%;
    animation: ${gradientShift} 2s ease infinite;
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    background: ${theme.colors.orangeGradient} !important;
    cursor: not-allowed;
  }

  div {
    cursor: inherit;
  }

  svg {
    margin-right: 0px;
  }

  @media (max-width: 768px) {
    height: 35px;
    width: 35px;
    padding: 18px;
    border-radius: 18px;
  }
`;

const ButtonsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
  justify-content: space-between;
  align-items: flex-end;

  @media (max-width: 768px) {
    justify-content: space-between;
    align-items: center;
    gap: 5px;
  }
`;

const ChatInterface = () => {
  const { t, isLoading } = useTranslate();
  const tolgee = useTolgee();
  const [inputValue, setInputValue] = useState('');
  const [isTextAreaFocused, setIsTextAreaFocused] = useState(false);
  const [isInputContainerHovered, setIsInputContainerHovered] = useState(false);
  const [typewriterText, setTypewriterText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const metrics = useMetrics(true);
  const router = useRouter();
  const { tenantId } = useRouterMatch();
  const session = useSession();
  const isAuthenticated = session.status === 'authenticated';

  // Get current language and direction
  const lang = tolgee.getLanguage() || 'en';
  const isRTL = rtlLangs.some(l => lang.startsWith(l));

  // Default lesson suggestions - store translation keys instead of translated values

  const examples = useMemo(
    () => [
      t(
        'prompt_example_1',
        '8th grade novel study on The Giver by Lois Lowry, chapter 5. Focus on theme development.',
        { noWrap: true },
      ),
      t(
        'prompt_example_2',
        'Novel study lesson for 10th graders. Things Fall Apart by Chinua Achebe, chapter 13, with a focus on plot development.',
        { noWrap: true },
      ),
      t(
        'prompt_example_3',
        'Macbeth Act III, Scene IV read aloud lesson. Analyze foreshadowing. 9th grade ELA.',
        { noWrap: true },
      ),
      t(
        'prompt_example_4',
        '4th grade read aloud lesson: Holes chapter 30, focus on conflict.',
        { noWrap: true },
      ),
      t(
        'prompt_example_5',
        '6th grade novel study on The Giver Chapter 3 focusing on making inferences.',
        { noWrap: true },
      ),
      t(
        'prompt_example_6',
        '4th grade writing lesson. Use sensory imagery to convey a sequence of events from summer break.',
        { noWrap: true },
      ),
    ],
    [t],
  );

  // Typewriter effect
  useEffect(() => {
    if (isLoading) return;
    if (isUserInteracting || inputValue.trim()) {
      setTypewriterText('');
      setShowCursor(false);
      return;
    }

    let exampleIndex = 0;
    let charIndex = 0;
    let isTyping = true;
    let pauseCounter = 0;
    let intervalId: NodeJS.Timeout;

    const step = () => {
      const currentText = examples[exampleIndex];

      if (isTyping) {
        // Typing phase
        if (charIndex < currentText.length) {
          // Safety limit
          setTypewriterText(currentText.substring(0, charIndex + 1));
          charIndex++;
        } else {
          // Done typing, start pause before deleting
          isTyping = false;
          pauseCounter = 0;
        }
      } else if (pauseCounter < 25) {
        // Pause for ~1000ms (25 * 40ms)
        pauseCounter++;
      } else {
        // Deleting phase
        if (charIndex > 0) {
          charIndex--;
          setTypewriterText(currentText.substring(0, charIndex));
        } else {
          // Done deleting, move to next example
          exampleIndex = (exampleIndex + 1) % examples.length;
          isTyping = true;
          pauseCounter = 0;
        }
      }
    };

    // Start after initial delay
    const initialTimeout = setTimeout(() => {
      intervalId = setInterval(step, 40); // 40ms interval for smooth animation
    }, 500);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isUserInteracting, inputValue, examples, isLoading]);

  // Autofocus on component mount (without triggering user interaction)
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Unified navigation handler that handles authentication and query parameters
  const navigateWithAuth = (queryParams?: Record<string, string>) => {
    const basePath = `/${tenantId}/create`;
    const trackingParams = getTrackingParams();

    if (isAuthenticated && session.data) {
      router.push({
        pathname: basePath,
        query: queryParams,
      });
    } else {
      // Build callback URL with query parameters
      const callbackUrl = new URL(basePath, window.location.origin);
      if (queryParams) {
        Object.entries(queryParams).forEach(([key, value]) => {
          callbackUrl.searchParams.set(key, value);
        });
      }

      // Prepare signup URL query parameters
      const signupQuery: Record<string, string> = {
        callbackUrl: callbackUrl.toString(),
      };

      // Add tracking parameters if present
      if (trackingParams.fbclid) {
        signupQuery.fbclid = trackingParams.fbclid;
      }
      if (trackingParams.ttclid) {
        signupQuery.ttclid = trackingParams.ttclid;
      }

      router.push({
        pathname: '/signup',
        query: signupQuery,
      });
    }
  };

  const handleSubmit = async () => {
    if (!inputValue.trim()) return;

    metrics.logEvent('LandingPage.SubmitPrompt', {
      lessonPrompt: inputValue,
    });

    navigateWithAuth({ prompt: inputValue.trim() });
    setInputValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleResourceSelect = () => {
    metrics.logEvent('LandingPage.ResourceSelect', {
      resourceType: 'upload',
      promptInput: inputValue,
    });

    navigateWithAuth();
  };

  const handleCategorySelect = (
    category: string,
    lessonCategory: string,
    eventName: string,
  ) => {
    metrics.logEvent(eventName, {
      category,
      lessonCategory,
    });

    navigateWithAuth({ category, lessonCategory });
  };

  const handleTestPrepSelect = () => {
    handleCategorySelect('test_prep_made_fun', 'test_prep', 'LandingPage.TestPrepSelect');
  };

  const handleQuickWritesSelect = () => {
    handleCategorySelect(
      'five_min_writing',
      'writing_skills',
      'LandingPage.QuickWritesSelect',
    );
  };

  const handleUserInteraction = () => {
    setIsUserInteracting(true);
  };

  const handleInputBlur = () => {
    // Reset user interaction when clicking outside and input is empty
    if (!inputValue.trim()) {
      setIsUserInteracting(false);
    }
    setIsTextAreaFocused(false);
  };

  // Dynamic placeholder text
  const getPlaceholderText = () => {
    if (isUserInteracting || inputValue.trim()) {
      return t(
        'Start by describing your lesson. For example: 5th grade, ELA, Characterization',
        'Start by describing your lesson. For example: 5th grade, ELA, Characterization',
      );
    }
    return typewriterText;
  };

  return (
    <Container>
      <FormContainer>
        <InputContainer
          $isTextAreaFocused={isTextAreaFocused}
          $isHovered={isInputContainerHovered}
          onMouseEnter={() => setIsInputContainerHovered(true)}
          onMouseLeave={() => setIsInputContainerHovered(false)}
          onClick={() => {
            handleUserInteraction();
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }}
        >
          <StyledTextAreaWrapper $isRTL={isRTL}>
            <StyledTextArea
              value={inputValue}
              onChange={e => {
                handleUserInteraction();
                setInputValue(e.target.value);
              }}
              onFocus={() => {
                setIsTextAreaFocused(true);
              }}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              placeholder={getPlaceholderText()}
              autoSize={{ minRows: 3, maxRows: 10 }}
              autoFocus={true}
              ref={inputRef}
              style={{
                minWidth: '100%',
              }}
            />
          </StyledTextAreaWrapper>

          <ButtonsBar>
            <Dropdown
              overlay={
                <Menu>
                  <StyledMenuItem
                    key="presentation"
                    onClick={() => {
                      handleResourceSelect();
                    }}
                  >
                    <IconWrapper height="16px" width="16px">
                      <MediaIconSvg />
                    </IconWrapper>
                    <span>
                      <T
                        keyName="Presentation (PPT, PDF, Google)"
                        defaultValue="Presentation (PPT, PDF, Google)"
                      />
                    </span>
                  </StyledMenuItem>
                  <Tooltip title={t('Coming Soon', 'Coming Soon')} placement="right">
                    <StyledMenuItem
                      key="youtube"
                      onClick={() => {
                        // Do nothing - disabled item
                      }}
                      style={{ opacity: 0.5, cursor: 'not-allowed' }}
                    >
                      <IconWrapper height="16px" width="16px">
                        <YoutubeSvg />
                      </IconWrapper>
                      <span>
                        <T keyName="Youtube link" defaultValue="Youtube link" />
                      </span>
                    </StyledMenuItem>
                  </Tooltip>
                  <Tooltip title={t('Coming Soon', 'Coming Soon')} placement="right">
                    <StyledMenuItem
                      key="upload"
                      onClick={() => {
                        // Do nothing - disabled item
                      }}
                      style={{ opacity: 0.5, cursor: 'not-allowed' }}
                    >
                      <IconWrapper height="16px" width="16px">
                        <UploadsSvg />
                      </IconWrapper>
                      <span>
                        <T keyName="Upload file" defaultValue="Upload file" />
                      </span>
                    </StyledMenuItem>
                  </Tooltip>
                  <Tooltip title={t('Coming Soon', 'Coming Soon')} placement="right">
                    <StyledMenuItem
                      key="text"
                      onClick={() => {
                        // Do nothing - disabled item
                      }}
                      style={{ opacity: 0.5, cursor: 'not-allowed' }}
                    >
                      <IconWrapper height="16px" width="16px">
                        <PasteSvg />
                      </IconWrapper>
                      <span>
                        <T keyName="Paste in text" defaultValue="Paste in text" />
                      </span>
                    </StyledMenuItem>
                  </Tooltip>
                </Menu>
              }
              trigger={['click']}
            >
              <MobileResourcesButton type="ghost" onClick={e => e.stopPropagation()}>
                <span className="button-text">
                  <T keyName="Use my materials" defaultValue="Use my materials" />
                </span>
                <IconWrapper height="15px" width="15px">
                  <PlusSvg />
                </IconWrapper>
              </MobileResourcesButton>
            </Dropdown>

            <RightButtonsContainer onClick={e => e.stopPropagation()}>
              <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item
                      style={{
                        background: theme.colors.lightestGreen,
                        color: theme.colors.black,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 10,
                      }}
                      key="fullLesson"
                      disabled={true}
                    >
                      <span
                        style={{
                          width: '100%',
                          minWidth: '200px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 10,
                        }}
                      >
                        <T keyName="Full Lesson" defaultValue="Full Lesson" />
                        <CheckSvg />
                      </span>
                    </Menu.Item>
                    <Menu.Item
                      key="quickWrites"
                      onClick={() => {
                        handleQuickWritesSelect();
                      }}
                    >
                      <T keyName="Quick Writes" defaultValue="Quick Writes" />
                    </Menu.Item>
                    <Menu.Item
                      key="testPrep"
                      onClick={() => {
                        handleTestPrepSelect();
                      }}
                    >
                      <T keyName="Test Prep" defaultValue="Test Prep" />
                    </Menu.Item>
                  </Menu>
                }
                trigger={['click']}
              >
                <LessonTypeDropdownButton type="ghost" onClick={e => e.stopPropagation()}>
                  <T keyName="Full lesson" defaultValue="Full lesson" />
                  <IconWrapper
                    style={{ transform: 'rotate(-90deg)' }}
                    height="16px"
                    width="16px"
                  >
                    <ArrowLeftSvg />
                  </IconWrapper>
                </LessonTypeDropdownButton>
              </Dropdown>

              <SubmitButton
                onClick={e => {
                  e.stopPropagation();
                  handleSubmit();
                }}
                disabled={!inputValue.trim()}
              >
                <IconWrapper
                  style={{ transform: 'rotate(-90deg)' }}
                  height="20px"
                  width="20px"
                  color={theme.colors.white}
                >
                  <ArrowDownSvg />
                </IconWrapper>
              </SubmitButton>
            </RightButtonsContainer>
          </ButtonsBar>
        </InputContainer>
      </FormContainer>
    </Container>
  );
};

const DropdownButton = styled(Button)`
  border: 1px solid ${theme.colors.greyLight};
  box-shadow: none;
  padding: 10px 15px;
  border-radius: 20px;
  background: none;
  height: 30px;
  display: flex;
  align-items: space-between;
  justify-content: center;
  gap: 5px;
  transition: all 0.2s ease;

  span {
    font-weight: 400;
  }
  svg {
    margin: 0px;
    height: 18px;
    width: 18px;
  }
`;

const LessonTypeDropdownButton = styled(DropdownButton)`
  border: 1px solid transparent;
  padding: 10px;
  height: 35px;
  border-radius: 10px;

  &:hover,
  &:focus {
    background-color: ${theme.colors.greyLight};
    border: 1px solid ${theme.colors.grey};
  }

  @media (max-width: 768px) {
    height: 32px;
    padding: 8px;
    font-size: 14px;
    color: ${theme.colors.greyDark};

    &:hover,
    &:focus {
      background-color: ${theme.colors.grey};
    }

    span {
      font-size: 14px;
      color: ${theme.colors.greyDark};
    }

    svg {
      color: ${theme.colors.greyDark};
    }
  }
`;

const MobileResourcesButton = styled(DropdownButton)`
  height: 30px;

  &:hover {
    box-shadow: ${theme.effects.floatingCreatorElementShadow};
    border: 1px solid transparent;
  }

  svg {
    height: 15px;
    width: 15px;
    margin: 0px;
    margin-left: 5px;
  }

  .button-text {
    @media (max-width: 768px) {
      display: none;
    }
  }

  @media (max-width: 768px) {
    min-width: 30px;
    width: 30px;
    height: 30px;
    padding: 8px;
    border-radius: 50%;
    border: 1px solid ${theme.colors.grey};

    &:hover {
      background-color: ${theme.colors.grey};
    }

    svg {
      margin-left: 0;
      color: ${theme.colors.greyDark};
    }
  }
`;

const RightButtonsContainer = styled.div`
  display: flex;
  gap: 10px;
  width: fit-content;
  flex-wrap: wrap;
  align-items: center;
  margin-left: auto;

  @media (max-width: 768px) {
    gap: 5px;
  }
`;

const StyledTextAreaWrapper = styled.div<{ $isRTL: boolean }>`
  .ant-input {
    &::placeholder {
      color: ${theme.colors.greyDark} !important;
    }
    ${({ $isRTL }) =>
      $isRTL &&
      `
      direction: rtl;
      text-align: right;
    `}
  }
`;

const StyledTextArea = styled(TextArea)`
  && {
    font-size: 18px;
    font-weight: 600;
    padding: 10px;
    border-radius: 0;
    border: none;
    background-color: transparent;

    /* Safari cursor fix */
    -webkit-text-fill-color: initial;
    caret-color: ${theme.colors.black};

    @media (max-width: 768px) {
      font-size: 18px;
      padding: 12px;
    }
  }
`;

const StyledMenuItem = styled(Menu.Item)`
  & > span {
    display: grid !important;
    grid-template-columns: 24px 1fr;
    gap: 12px;
    align-items: center;

    &:hover {
      background-color: ${theme.colors.greyLight} !important;
    }

    .ant-dropdown-menu-item-icon {
      margin-right: 0 !important;
    }
  }
`;

export default ChatInterface;
